import { useCallback, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { AxiosError } from 'axios'
import dayjs from 'dayjs'
import { orderSearchResultToHead, toReturnWorkScanRow } from '@/services/return/returnWorkScan/mappers'
import {
  ReturnOrderSearchRes,
  ReturnWorkScanHead,
  ReturnWorkScanHeadFilter,
  ReturnWorkScanItemFilter,
  ReturnWorkScanRow,
  ReturnWorkScanSaveHeadReq,
  ReturnWorkScanSaveItemReq,
  ReturnWorkScanSaveReq,
  ReturnWorkScanSaveRes,
} from '@/services/return/returnWorkScan/types'
import {
  useGetReturnWorkScanItems,
  useGetReturnWorkScanSummary,
  useSaveReturnWorkScan,
} from '@/services/return/returnWorkScan/useReturnWorkScanQueries'
import { ACT_DATE_RANGE_DAYS, MESSAGES, ReturnWorkFilters } from '../constants'
import { scanNotice } from '@/components/pbo/notice'
import { toReturnShipper } from '@/pages/return/coMingle'

/** 바코드 칸에 이 값을 치면(1.0 동일, 스캐너 저장 용도) 저장을 실행한다. */
const SAVE_KEYWORD = 'SAVE'

const EMPTY_HEAD: ReturnWorkScanHead = {
  shipperCode: '',
  returnInvoiceNumber: '',
  originalInvoiceNumber: '',
  orderName: '',
  receiverName: '',
  receiverPhone: '',
  receiverAddress: '',
  receiverZip: '',
}

const actDateRange = () => ({
  actDateFrom: dayjs().subtract(ACT_DATE_RANGE_DAYS, 'day').format('YYYYMMDD'),
  actDateTo: dayjs().format('YYYYMMDD'),
})

interface HeadSearchGuardInput {
  returnInvoiceNumber: string
  originalInvoiceNumber: string
  hasResolvedHead: boolean
  isManualEntry: boolean
}

/**
 * HEAD 조회를 막는 이유. 조회할 수 있으면 null 이다.
 *
 * 가드 자체는 1.0 `onSearchInvoice`(RGR06.js:238 — `REF_NO3`·`TRS_INVOICE_NO` 가 둘 다 있고
 * `invoiceData` 가 비었을 때만 조회) 그대로다. 반송장만으로 찾는 경로는 이 화면 계약에 없다 —
 * HEAD API 는 `trsInvoiceNo`(원송장) 로만 검색하고 파라미터 4개가 전부 필수다
 * (moms-be `ReturnWorkScanController.getWorkScanHeads`). rgr18 의 `GET /api/v1/return/work-invoices`
 * 는 `rtnInvoiceNo` 를 받지만 RGR11 전용 계약이라 이 화면이 쓸 수 없다.
 *
 * 달라진 것은 막는 이유를 돌려준다는 점뿐이다(LOGISPRD-12611). 예전에는 넷 다 조용히 return 해서
 * 반송장에 값을 넣고 Enter 를 쳐도 빈 값으로 쳤을 때와 결과가 같았다.
 */
export const headSearchBlockReason = ({
  returnInvoiceNumber,
  originalInvoiceNumber,
  hasResolvedHead,
  isManualEntry,
}: HeadSearchGuardInput): string | null => {
  if (isManualEntry) return MESSAGES.manualEntryNoSearch
  if (hasResolvedHead) return MESSAGES.headAlreadyLoaded
  if (!returnInvoiceNumber) return MESSAGES.returnInvoiceRequired
  if (!originalInvoiceNumber) return MESSAGES.originalInvoiceRequired
  return null
}

interface UseReturnWorkScanOptions {
  shipperCode: string
  coMingleEnabled?: boolean
  filters: ReturnWorkFilters
  /** 저장이 성공하고 신규 등록 행이 1건 이상일 때 호출된다(8단계 파일 업로드 트리거). */
  onSaved?: (result: ReturnWorkScanSaveRes, savedShipperCode?: string) => void
  /** [저장] 필수 칸 — 화면관리 그리드 설정이 필수로 켠 저장 편집 칸(LOGISPRD-17253). 없으면 검사하지 않는다(지금 동작) */
  requiredFields?: ReadonlyArray<RequiredField>
}

export interface RequiredField {
  key: keyof ReturnWorkScanRow
  title: string
}

const NO_REQUIRED: ReadonlyArray<RequiredField> = []

export const useReturnWorkScan = ({ shipperCode, coMingleEnabled = false, filters, onSaved, requiredFields = NO_REQUIRED }: UseReturnWorkScanOptions) => {
  const queryShipper = toReturnShipper(shipperCode, coMingleEnabled)
  const [returnInvoiceNumber, setReturnInvoiceNumber] = useState('')
  const [originalInvoiceNumber, setOriginalInvoiceNumber] = useState('')
  const [isManualEntry, setIsManualEntry] = useState(false)
  const [manualHead, setManualHead] = useState<ReturnWorkScanHead>(EMPTY_HEAD)
  /** 수기입력사유 — 원송장 없이 저장할 때 필수(3장 3절, GBGH·BUDHIMUDRA 전용 예외). */
  const [jobCommentForInvoice, setJobCommentForInvoice] = useState('')
  const [resolvedHead, setResolvedHead] = useState<ReturnWorkScanHead | null>(null)
  const [nextOrderNumber, setNextOrderNumber] = useState<number | null>(null)
  const [rows, setRows] = useState<ReturnWorkScanRow[]>([])

  const [headFilter, setHeadFilter] = useState<ReturnWorkScanHeadFilter | null>(null)
  const headQuery = useGetReturnWorkScanSummary(headFilter)

  const [itemFilter, setItemFilter] = useState<ReturnWorkScanItemFilter | null>(null)
  const itemQuery = useGetReturnWorkScanItems(itemFilter)
  const { refetch: refetchItemQuery } = itemQuery

  /**
   * HEAD 조회 요청 순서 가드. 원송장을 연속으로 고쳐 스캔하면 나중에 보낸 요청의 응답이 먼저
   * 온 요청보다 먼저 도착할 수 있다 — 이 값과 다른 seq 로 도착한 응답은 버린다.
   */
  const headRequestSeqRef = useRef(0)

  /**
   * 반송장·원송장 두 값이 모두 채워지고 아직 HEAD 를 못 받았을 때만 조회한다(`headSearchBlockReason`).
   * HEAD 를 받았으면 true 로 끝난다 — 컴포넌트 모드 뷰가 성공 토스트('송장 정보를 불러왔습니다.')를 이 결과로 띄운다.
   * 막힘·0건·실패는 여기서 안내하고 false 다(뷰는 토스트를 생략한다).
   */
  const maybeFetchHead = useCallback(
    (nextReturnInvoiceNumber: string, nextOriginalInvoiceNumber: string): Promise<boolean> => {
      const blockReason = headSearchBlockReason({
        returnInvoiceNumber: nextReturnInvoiceNumber,
        originalInvoiceNumber: nextOriginalInvoiceNumber,
        hasResolvedHead: resolvedHead != null,
        isManualEntry,
      })
      if (blockReason) {
        // 확인을 눌러야 닫히는 안내창이 아니라 토스트다. 네 이유 중 셋(반송장만 스캔·수기입력·이미 조회함)은 오류가 아니라
        // 스캔 동선의 통상 상태이고, 특히 반송장만 스캔한 상태는 정상 흐름에서 매 건 지나간다.
        // 여기에 모달을 띄우면 다음 스캔이 모달에 막힌다(LOGISPRD-12578 rgr14 결함 4·rgr18 관찰).
        // 진짜 실패(HEAD 0건·조회 오류)도 큰 토스트다(스캔 화면 예외 — scanNotice, 알럿을 쓰지 않는다).
        scanNotice.invalid(blockReason)
        return Promise.resolve(false)
      }

      const seq = ++headRequestSeqRef.current
      const filter: ReturnWorkScanHeadFilter = { shipper: queryShipper, trsInvoiceNo: nextOriginalInvoiceNumber, ...actDateRange() }
      flushSync(() => setHeadFilter(filter))
      return headQuery
        .refetch()
        .then(({ data, error }) => {
          if (seq !== headRequestSeqRef.current) return false
          // 서버 오류는 "주문 내역 없음" 이 아니라 조회 실패로 알린다(전역 알림을 끈 쿼리라 여기서 한 번).
          if (error) {
            scanNotice.failure(MESSAGES.searchFailed)
            return false
          }
          if (!data || !data.head) {
            scanNotice.failure(MESSAGES.headNotFound)
            return false
          }
          setResolvedHead({ ...data.head, returnInvoiceNumber: nextReturnInvoiceNumber })
          setNextOrderNumber(data.nextOrderNumber)
          return true
        })
        // refetch 는 reject 하지 않는다(react-query v5) — 실패는 위 then 의 error 가 알린다. 여기선 no-floating-promises 규칙만 닫는다.
        .catch(() => false)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryShipper, resolvedHead, isManualEntry],
  )

  /** 반송장 스캔 — HEAD 를 받았으면 true 로 끝난다(컴포넌트 모드 뷰의 성공 토스트용). */
  const scanReturnInvoice = useCallback(
    (value: string): Promise<boolean> => {
      setReturnInvoiceNumber(value)
      return maybeFetchHead(value, originalInvoiceNumber)
    },
    [maybeFetchHead, originalInvoiceNumber],
  )

  /** 원송장 스캔 — HEAD 를 받았으면 true 로 끝난다(컴포넌트 모드 뷰의 성공 토스트용). */
  const scanOriginalInvoice = useCallback(
    (value: string): Promise<boolean> => {
      setOriginalInvoiceNumber(value)
      return maybeFetchHead(returnInvoiceNumber, value)
    },
    [maybeFetchHead, returnInvoiceNumber],
  )

  // 결과를 기다리지 않는 호출자(Enter 핸들러)용 — 반환값이 없어야 act(() => …) 가 동기 경로로 돈다.
  const handleReturnInvoiceScan = useCallback((value: string) => {
    scanReturnInvoice(value).catch(() => undefined)
  }, [scanReturnInvoice])

  const handleOriginalInvoiceScan = useCallback((value: string) => {
    scanOriginalInvoice(value).catch(() => undefined)
  }, [scanOriginalInvoice])

  /**
   * 주문검색 결과 선택 — 수취인 정보는 HEAD 를 다시 조회하지 않고 고른 행을 그대로 헤더로 쓴다
   * (5단계, 1.0 `handleSelect` 동일). 다음 반품번호(`ordNoMax`)는 수취인 정보와 출처가 달라
   * 별도로 받는다 — 선택한 행의 송장번호로 HEAD 조회를 한 번 더 태우되 그 응답의 수취인 정보는
   * 버리고 `nextOrderNumber` 만 반영한다.
   */
  const applyOrderSearchResult = useCallback(
    (result: ReturnOrderSearchRes): boolean => {
      const head = orderSearchResultToHead(result)
      // 저장은 표의 전 행을 마지막 HEAD 의 원본 화주로 보낸다. 스캔한 행이 있으면 다른 원본 화주의 원주문으로 바꾸지 않는다.
      const currentShipper = (!isManualEntry && resolvedHead?.shipperCode) || shipperCode
      if (coMingleEnabled && rows.length > 0 && !!head.shipperCode && head.shipperCode !== currentShipper) return false
      setResolvedHead({ ...head, returnInvoiceNumber })
      setOriginalInvoiceNumber(result.trsInvoiceNo ?? '')

      if (!result.trsInvoiceNo) return true
      const seq = ++headRequestSeqRef.current
      const filter: ReturnWorkScanHeadFilter = { shipper: queryShipper, trsInvoiceNo: result.trsInvoiceNo, ...actDateRange() }
      flushSync(() => setHeadFilter(filter))
      headQuery
        .refetch()
        .then(({ data, error }) => {
          if (seq !== headRequestSeqRef.current) return
          // v5 refetch 는 실패해도 reject 하지 않고 { error } 로 resolve 한다 — 전역 알림을 끈 쿼리라 여기서 한 번 알린다(#899 리뷰).
          // 선택한 HEAD 는 이미 반영했으니 그대로 두고 계속 작업할 수 있다(다음 반품번호만 못 받음).
          if (error) {
            scanNotice.failure(MESSAGES.searchFailed)
            return
          }
          if (data?.nextOrderNumber != null) setNextOrderNumber(data.nextOrderNumber)
        })
        .catch(() => {
          // no-floating-promises 용 — refetch 는 reject 하지 않는다(실패는 위 error 로 온다).
        })
      return true
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [returnInvoiceNumber, queryShipper, coMingleEnabled, rows.length, isManualEntry, resolvedHead, shipperCode],
  )

  /**
   * 새 행 추가 시 직전 행의 반품처를 이어받는다 — 1.0 `onRowAddOption={['PLN_DATE','ORD_OPT_NO',
   * 'RL_CHANNEL','ORD_NO']}`(RGR06.js:909) 재현. PLN_DATE·ORD_OPT_NO는 이 화면에 대응 컬럼이
   * 없고 ORD_NO는 이미 별도로 채번하므로, 실제로 옮길 값은 반품처뿐이다.
   */
  const appendRow = useCallback((itemRes: Parameters<typeof toReturnWorkScanRow>[0]) => {
    setNextOrderNumber((current) => {
      const orderNumber = current ?? 1
      setRows((prevRows) => {
        const key = `${Date.now()}-${prevRows.length}`
        const row = toReturnWorkScanRow(itemRes, String(orderNumber), key)
        const prevRlChannel = prevRows.at(-1)?.rlChannel
        return [...prevRows, prevRlChannel ? { ...row, rlChannel: prevRlChannel } : row]
      })
      return orderNumber + 1
    })
  }, [])

  const reset = useCallback(() => {
    setReturnInvoiceNumber('')
    setOriginalInvoiceNumber('')
    setIsManualEntry(false)
    setManualHead(EMPTY_HEAD)
    setJobCommentForInvoice('')
    setResolvedHead(null)
    setNextOrderNumber(null)
    setRows([])
    setHeadFilter(null)
    setItemFilter(null)
  }, [])

  const saveMutation = useSaveReturnWorkScan()
  // 저장 진행 중 잠금 — [검수완료]·바코드 SAVE 어느 경로든 여기서 막는다. isPending 은 다음 렌더에야 참이라 같은 틱 연타를 못 막는다(LOGISPRD-14894)
  const savingRef = useRef(false)

  /** 저장 — 선택 여부와 무관하게 그리드 전 행을 보낸다(1.0 이 저장 직전 selectAll 을 부르는 것과 같은 결과). */
  const handleSave = useCallback(async () => {
    if (savingRef.current) return
    if (rows.length === 0) {
      scanNotice.invalid(MESSAGES.noRowsToSave)
      return
    }
    // 반품번호는 표에서 고칠 수 있다(LOGISPRD-14907) — 비었거나 숫자가 아니면 보내지 않는다
    if (rows.some((row) => !/^\d+$/.test(row.orderNumber))) {
      scanNotice.invalid(MESSAGES.orderNumberInvalid)
      return
    }
    // 1.0 RGR06 은 저장 직전 CommonUtil.onSaveData 가 required 칸이 빈 행을 막았다(RGR06.js:384) — 전 행을 보내므로 전 행을 본다
    for (const [index, row] of rows.entries()) {
      const missing = requiredFields.find((field) => String(row[field.key] ?? '').trim() === '')
      if (missing) {
        scanNotice.rowInvalid(MESSAGES.requiredMissing(index + 1, missing.title))
        return
      }
    }

    const source = isManualEntry ? manualHead : (resolvedHead ?? EMPTY_HEAD)
    const head: ReturnWorkScanSaveHeadReq = {
      shipperCode: (coMingleEnabled && !isManualEntry && source.shipperCode) || shipperCode,
      returnInvoiceNumber,
      // 원송장은 수기입력 여부와 무관하게 훅 상태를 그대로 쓴다 — 원송장 스캔 칸은 1.0 과 같이
      // 수기입력 전환과 별개로 항상 떠 있다(`ScanPanel.tsx`). manualHead 에는 원송장을 채울 통로가
      // 없어 source.originalInvoiceNumber 를 쓰면 수기입력 모드에서 늘 빈 값이 나갔다.
      originalInvoiceNumber: originalInvoiceNumber || undefined,
      receiverAddress: source.receiverAddress || undefined,
      receiverPhone: source.receiverPhone || undefined,
      receiverName: source.receiverName || undefined,
      jobCommentForInvoice: jobCommentForInvoice || undefined,
      isSendToDoosonERP: filters.isSendToDoosonERP,
      fileId: null,
    }
    const items: ReturnWorkScanSaveItemReq[] = rows.map((row) => ({
      orderNumber: row.orderNumber,
      orderQuantity: row.orderQuantity,
      barcode: row.barcode,
      goodsCode: row.goodsCode,
      goodsName: row.goodsName,
      goodsOption: row.goodsOption,
      goodsColor: row.goodsColor,
      goodsGroup1: row.goodsGroup1,
      goodsAttribute1: row.goodsAttribute1,
      brandName: row.brandName,
      styleNumber: row.styleNumber,
      salesChannel: row.salesChannel,
      referenceNumber1: row.referenceNumber1,
      referenceNumber2: row.referenceNumber2,
      condition: row.condition,
      procurementName: row.procurementName,
      etc1: row.etc1,
      etc2: row.etc2,
      etc3: row.etc3,
      etc4: row.etc4,
      logisticsProviderName: row.logisticsProviderName,
    }))
    const body: ReturnWorkScanSaveReq = { head, items }

    savingRef.current = true
    try {
      const result = await saveMutation.mutateAsync(body)
      if (result && result.inserted.length > 0) {
        if (coMingleEnabled) onSaved?.(result, head.shipperCode)
        else onSaved?.(result)
      }

      const duplicates = result?.duplicateOrderNumbers ?? []
      if (duplicates.length > 0) {
        const duplicateSet = new Set(duplicates)
        // 저장된 행은 빼고 중복으로 빠진 행만 남긴다 — 남겨 두면 번호를 고쳐 다시 저장할 때 그 행들이 중복으로 걸린다(LOGISPRD-14907).
        //  판정은 보낸 시점 번호로 한다 — 응답 대기 중 스캔한 행(보내지 않음)이나 그사이 번호를 고친 행을 지우지 않게
        const sentNumberByKey = new Map(rows.map((row) => [row.key, row.orderNumber]))
        setRows((prev) =>
          prev.flatMap((row) => {
            const sent = sentNumberByKey.get(row.key)
            if (sent === undefined) return [row]
            return duplicateSet.has(sent) ? [{ ...row, duplicate: row.orderNumber === sent }] : []
          }),
        )
        scanNotice.failure(MESSAGES.saveSuccessWithDuplicates(duplicates.length))
        return
      }
      scanNotice.success(MESSAGES.saveSuccess)
      reset()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
      const status = (e as AxiosError)?.response?.status
      if (status === 403) {
        scanNotice.failure(MESSAGES.shipperUnauthorized)
      } else if (status === 400) {
        scanNotice.failure(MESSAGES.invoiceOrCommentRequired)
      } else {
        scanNotice.failure(MESSAGES.saveFailed)
      }
    } finally {
      savingRef.current = false
    }
  }, [
    rows,
    isManualEntry,
    manualHead,
    resolvedHead,
    shipperCode,
    coMingleEnabled,
    returnInvoiceNumber,
    originalInvoiceNumber,
    jobCommentForInvoice,
    filters.isSendToDoosonERP,
    saveMutation,
    onSaved,
    reset,
    requiredFields,
  ])

  /** 바코드 Enter — SAVE 면 저장을, 아니면 ITEM 조회를 실행한다. */
  const handleBarcodeScan = useCallback(
    (value: string) => {
      const trimmed = value.trim()
      if (!trimmed) return

      if (trimmed.toUpperCase() === SAVE_KEYWORD) {
        handleSave()
        return
      }

      const flag = filters.masterFlag === 'Y'
      if (!flag && (!returnInvoiceNumber || !originalInvoiceNumber)) {
        scanNotice.invalid(MESSAGES.itemScanRequired)
        return
      }

      const filter: ReturnWorkScanItemFilter = {
        shipper: queryShipper,
        barcode: trimmed,
        trsInvoiceNo: originalInvoiceNumber,
        flag,
        ...actDateRange(),
      }
      flushSync(() => setItemFilter(filter))
      refetchItemQuery()
        .then(({ data, error }) => {
          if (error) {
            scanNotice.failure(MESSAGES.searchFailed) // 서버 오류 — "바코드 내역 없음" 과 구분
            return
          }
          const first = data?.[0]
          if (first) {
            appendRow(first)
          } else {
            scanNotice.failure(MESSAGES.itemNotFound)
          }
        })
        .catch(() => undefined) // refetch 는 reject 하지 않는다 — 실패는 위 then 의 error 가 알린다(no-floating-promises 규칙만 닫는다)
    },
    [filters.masterFlag, returnInvoiceNumber, originalInvoiceNumber, queryShipper, refetchItemQuery, appendRow, handleSave],
  )

  const updateRow = useCallback((key: string, patch: Partial<ReturnWorkScanRow>) => {
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch } : row)))
  }, [])

  const onRowsChange = useCallback((changed: ReturnWorkScanRow[]) => {
    setRows((prev) => {
      const patched = new Map(changed.map((row) => [row.key, row]))
      return prev.map((row) => patched.get(row.key) ?? row)
    })
  }, [])

  const head = useMemo(() => (isManualEntry ? manualHead : (resolvedHead ?? EMPTY_HEAD)), [isManualEntry, manualHead, resolvedHead])

  return {
    returnInvoiceNumber,
    originalInvoiceNumber,
    /** 조회 없이 원송장 값만 바꾼다 — 수기입력 모드의 송장 칸(컴포넌트 모드 뷰는 수기입력 그리드에 송장 칸이 있다). */
    setOriginalInvoiceNumber,
    handleReturnInvoiceScan,
    handleOriginalInvoiceScan,
    scanReturnInvoice,
    scanOriginalInvoice,
    isManualEntry,
    setIsManualEntry,
    manualHead,
    setManualHead,
    jobCommentForInvoice,
    setJobCommentForInvoice,
    head,
    resolvedHead,
    isHeadLoading: headQuery.isFetching,
    nextOrderNumber,
    applyOrderSearchResult,
    rows,
    setRows,
    updateRow,
    onRowsChange,
    handleBarcodeScan,
    isItemLoading: itemQuery.isFetching,
    handleSave,
    isSaving: saveMutation.isPending,
    reset,
  }
}

export type UseReturnWorkScanResult = ReturnType<typeof useReturnWorkScan>
